import { motion, useReducedMotion } from "motion/react";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { useVisualEffects } from "../../hooks/VisualEffectsContext";

type Direction = "vertical" | "horizontal";

export function AnimatedContent({
  children,
  distance = 14,
  direction = "vertical",
  reverse = false,
  duration = 0.32,
  delay = 0,
  initialOpacity = 0,
  scale = 0.985,
  className = "",
  ...props
}: Omit<ComponentPropsWithoutRef<typeof motion.div>, "children"> & {
  children: ReactNode;
  distance?: number;
  direction?: Direction;
  reverse?: boolean;
  duration?: number;
  delay?: number;
  initialOpacity?: number;
  scale?: number;
}) {
  const systemReduced = useReducedMotion();
  const { reduced } = useVisualEffects();
  const reduceMotion = reduced || systemReduced;
  const offset = reverse ? -distance : distance;
  const initial = reduceMotion
    ? { opacity: 1, x: 0, y: 0, scale: 1 }
    : {
        opacity: initialOpacity,
        x: direction === "horizontal" ? offset : 0,
        y: direction === "vertical" ? offset : 0,
        scale,
      };

  return (
    <motion.div
      {...props}
      className={className}
      initial={initial}
      animate={{ opacity: 1, x: 0, y: 0, scale: 1 }}
      exit={reduceMotion ? undefined : { opacity: 0, y: -8, scale: 0.99 }}
      transition={{ duration: reduceMotion ? 0 : duration, delay: reduceMotion ? 0 : delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}
